/**
 * SPRINT-035: Hosted Checkout UI types
 * Mirror of backend CheckoutSessionPublic + AttemptResult shapes.
 */

export interface CheckoutSessionItem {
  id: string;
  product_id: string;
  price_id: string;
  product_name: string;
  price_label: string | null;
  quantity: number;
  unit_amount: number;
  amount_subtotal: number;
  currency: string;
}

export interface CheckoutSession {
  id: string;
  tenant_id: string;
  project_id: string;
  merchant_id: string;
  mode: string;
  amount: string;
  currency: string;
  customer_id: string | null;
  order_reference: string | null;
  status: 'open' | 'payment_initiated' | 'completed' | 'expired' | 'cancelled';
  allowed_product_keys: string[];
  success_url: string;
  cancel_url: string;
  ui_config: Record<string, unknown>;
  metadata: Record<string, unknown>;
  expires_at: string;
  completed_at: string | null;
  current_attempt_id: string | null;
  completed_attempt_id: string | null;
  created_at: string;
  updated_at: string;
  /**
   * Canonical runtime state per ADR-098. Authoritative when non-null.
   * Falls back to session.status for pre-migration sessions.
   */
  runtime_state: string | null;
  runtime_state_updated_at: string | null;
  // Merchant display name — enriched by session GET, not stored on session row
  merchant_display_name?: string | null;
  // Wallet balance for the identified customer — enriched by session GET
  wallet_balance?: string | null;
  wallet_currency?: string | null;
  // Populated when a wallet reservation is active (apply-wallet was called)
  payable_amount?: string | null;
  wallet_applied_amount?: string | null;
  // Populated when status = 'payment_initiated' — used for client-side recovery
  current_attempt?: {
    id: string;
    next_action: NextAction;
  } | null;
  // Only present on createSession response
  client_secret?: string;
  checkout_url?: string;
  // Product-backed sessions: snapshot of line items at creation time
  items?: CheckoutSessionItem[];
}

export interface NextAction {
  type: 'redirect' | 'qr' | 'embedded' | 'wait' | 'none' | 'manual_reference' | string;
  // Nested payload (used by some providers)
  data?: {
    redirect_url?: string;
    url?: string;
    qr_code_data?: string;
    qr_code_image?: string;
    embed_url?: string;
    reference?: string;
    instructions?: string;
    [key: string]: unknown;
  };
  // Movii Llave Dinámica flat fields (not nested under data)
  transferKey?: string;
  keyType?: string;
  currency?: string;
  rail?: string;
  expiresAt?: string;
  [key: string]: unknown;
}

export interface AttemptResult {
  attempt_id: string;
  attempt_no: number;
  session_id: string;
  session_status: string;
  payment_id: string | null;
  payment_status: string;
  next_action: NextAction;
  idempotent?: boolean;
}

export interface FieldDef {
  key: string;
  label: string;
  type: 'tel' | 'text' | 'email' | 'number' | 'select';
  placeholder?: string;
  hint?: string;
  required: boolean;
  options?: Array<{ value: string; label: string }>;
}

/** Stored payment method attached to a PaymentOption when customerId is on the session. */
export interface SavedPaymentMethod {
  paymentMethodId: string;
  displayLabel: string;
  brand?: string;
  last4?: string;
  provider?: string;
  isDefault?: boolean;
}

/**
 * Top-level one-tap candidate from the discovery response.
 * When present, the saved method should be pre-selected at the top of the method list.
 */
export interface SavedMethodOneTap {
  paymentMethodId: string;
  displayLabel: string;
  brand?: string;
  last4?: string;
  methodType?: string;
  provider?: string;
  isDefault?: boolean;
  mode?: 'confirm' | 'instant';
}

/** Mirror of backend PaymentMethodOption — camelCase throughout. */
export interface PaymentOption {
  methodKey: string;
  label: string;
  methodType?: string;
  provider?: string;
  rail?: string;
  eligible: boolean;
  requiresTokenization?: boolean;
  estimatedSpeed?: 'instant' | 'same_day' | 'next_day' | 'unknown';
  /** Supported delivery channel strings (e.g. ['qr', 'reference']). Only supported channels present. */
  channels?: string[];
  requiredFields?: FieldDef[];
  /**
   * Present on card methods when Wompi tokenization is configured for this tenant.
   * publicKey is safe to expose — it is not a secret.
   * [INVARIANT 18] Key is tenant-scoped and environment-scoped.
   */
  tokenizationConfig?: {
    provider: 'wompi';
    publicKey: string;
    environment: 'sandbox' | 'production';
  };
  metadata?: Record<string, unknown>;
  /** Saved payment methods for this option — only present when session has a customerId. */
  savedMethods?: SavedPaymentMethod[];
}

export interface CheckoutError {
  code: string;
  message: string;
  recoverable: boolean;
}

/** Pick the best supported channel type from a payment option's channels array. */
export function pickChannelType(option: PaymentOption): 'redirect' | 'qr' | 'embedded' | 'reference' {
  const supported = (ch: string) => (option.channels ?? []).includes(ch);
  // Priority: embedded > redirect > qr > reference
  if (supported('embedded')) return 'embedded';
  if (supported('redirect')) return 'redirect';
  if (supported('qr')) return 'qr';
  return 'reference';
}

/** Resolve the redirect URL from a next_action object (adapters use different keys). */
export function getRedirectUrl(nextAction: NextAction): string | null {
  return (nextAction.data?.redirect_url as string | undefined)
    ?? (nextAction.data?.url as string | undefined)
    ?? (nextAction.redirectUrl as string | undefined)
    ?? (nextAction.redirect_url as string | undefined)
    ?? null;
}
